import { useState } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { 
  Download, 
  Copy, 
  Share2, 
  Smartphone, 
  Monitor, 
  CheckCircle2,
  ExternalLink
} from "lucide-react";

// Platform Detection
const detectPlatform = () => {
  const ua = navigator.userAgent;
  const platform = navigator.platform;
  
  if (/iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  if (platform.toLowerCase().includes('mac') && navigator.maxTouchPoints <= 1) {
    return 'macos';
  }
  if (/Android/.test(ua)) {
    return 'android';
  }
  if (/Win/.test(platform)) {
    return 'windows';
  }
  return 'other';
};

// Copy to clipboard
const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
    return true;
  } catch (err) {
    toast.error("Failed to copy");
    return false;
  }
};

// Universal download function
const downloadMedia = async (url, filename, setDownloading) => {
  const platform = detectPlatform();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const isFull = url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:');
  const fullUrl = isFull ? url : `${backendUrl}${url}`;

  // Determine file extension
  let ext = '.jpg';
  let kind = 'image';
  const dataMime = url.startsWith('data:') ? (url.slice(5).split(/[;,]/)[0] || '') : '';
  if (dataMime.startsWith('video/')) { ext = '.mp4'; kind = 'video'; }
  else if (dataMime === 'image/png') ext = '.png';
  else if (dataMime === 'image/gif') ext = '.gif';
  else if (url.match(/\.(mp4|webm|mov)(\?|$)/i)) { ext = '.mp4'; kind = 'video'; }
  else if (url.match(/\.gif(\?|$)/i)) ext = '.gif';
  else if (url.match(/\.png(\?|$)/i)) ext = '.png';

  const finalFilename = (filename || 'ama-content') + ext;

  try {
    setDownloading?.(true);

    // iOS: route through the Web Share API for the native "Save Image" /
    // "Save to Files" / "Save to Drive" sheet. Falls back to opening in
    // a new tab if Share API isn't available on this device.
    if (platform === 'ios') {
      // Lazy-import so this module stays small for non-iOS users.
      const { iosSave } = await import('../lib/iosSave');
      await iosSave(fullUrl, { kind, filename: finalFilename });
      toast.success(kind === 'image' ? '📱 Choose "Save Image" to send it to Photos' : '📱 Choose "Save to Files" or "Save to Drive"', { duration: 5000 });
      return true;
    }

    // All other platforms: Download as blob
    toast.info("Preparing download...");
    
    const response = await fetch(fullUrl, { mode: 'cors' });
    if (!response.ok) throw new Error('Download failed');
    
    const blob = await response.blob();
    const mimeType = ext === '.mp4' ? 'video/mp4' : blob.type;
    const mediaBlob = new Blob([blob], { type: mimeType });
    const blobUrl = window.URL.createObjectURL(mediaBlob);
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    link.style.display = 'none';
    
    if (platform === 'android') {
      link.setAttribute('target', '_blank');
    }
    
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 1000);
    
    toast.success("Download started!");
    return true;
    
  } catch (err) {
    console.error('Download error:', err);
    window.open(fullUrl, '_blank');
    toast.info("Opening in new tab - right-click or long-press to save");
    return false;
  } finally {
    setDownloading?.(false);
  }
};

// Share using Web Share API
const shareMedia = async (url, title, text) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = url.startsWith('http') ? url : `${backendUrl}${url}`;
  
  if (navigator.share) {
    try {
      await navigator.share({
        title: title || 'XEA Content',
        text: text || 'Check out this content!',
        url: fullUrl
      });
      toast.success("Shared successfully!");
      return true;
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error("Sharing failed");
      }
      return false;
    }
  } else {
    await copyToClipboard(fullUrl, "Link");
    return true;
  }
};

/**
 * ManualDownloadPanel - A dedicated panel for downloading and copying content
 * 
 * @param {Object} props
 * @param {string} props.mediaUrl - URL of the media to download
 * @param {string} props.caption - Caption text to copy
 * @param {string[]} props.hashtags - Array of hashtags
 * @param {string} props.platform - 'instagram' or 'tiktok'
 * @param {string} props.productUrl - Optional product URL to include
 * @param {string} props.variant - 'full' | 'compact' | 'minimal'
 */
const ManualDownloadPanel = ({ 
  mediaUrl, 
  caption, 
  hashtags = [], 
  platform = 'instagram',
  productUrl,
  variant = 'full'
}) => {
  const [downloading, setDownloading] = useState(false);
  const [copiedCaption, setCopiedCaption] = useState(false);
  const [copiedHashtags, setCopiedHashtags] = useState(false);
  
  const userPlatform = detectPlatform();
  const isMobile = userPlatform === 'ios' || userPlatform === 'android';
  
  // Build full caption with hashtags
  const getFullCaption = () => {
    let fullText = caption || "";
    if (hashtags?.length > 0) {
      const formattedTags = hashtags.map(tag => 
        tag.startsWith('#') ? tag : `#${tag}`
      ).join(' ');
      fullText += "\n\n" + formattedTags;
    }
    if (productUrl) {
      fullText += `\n\n🔗 ${productUrl}`;
    }
    return fullText;
  };
  
  const handleCopyCaption = async () => {
    const success = await copyToClipboard(caption, "Caption");
    if (success) {
      setCopiedCaption(true);
      setTimeout(() => setCopiedCaption(false), 2000);
    }
  };
  
  const handleCopyHashtags = async () => {
    const formattedTags = hashtags.map(tag => 
      tag.startsWith('#') ? tag : `#${tag}`
    ).join(' ');
    const success = await copyToClipboard(formattedTags, "Hashtags");
    if (success) {
      setCopiedHashtags(true);
      setTimeout(() => setCopiedHashtags(false), 2000);
    }
  };
  
  const handleCopyAll = async () => {
    await copyToClipboard(getFullCaption(), "Caption & hashtags");
  };
  
  const handleDownload = () => {
    downloadMedia(mediaUrl, `ama-${platform}-content`, setDownloading);
  };
  
  const handleShare = () => {
    shareMedia(mediaUrl, `XEA ${platform} Content`, caption);
  };
  
  // Get platform-specific instructions
  const getInstructions = () => {
    const platformName = platform === 'instagram' ? 'Instagram' : 'TikTok';
    
    switch (userPlatform) {
      case 'ios':
        return {
          icon: <Smartphone className="w-4 h-4" />,
          title: "iPhone/iPad",
          steps: [
            `Tap "Download Media"`,
            "Long-press the media",
            `Tap "Save to Photos"`,
            `Open ${platformName} and create a post`,
            "Select from your gallery",
            `Tap "Copy Caption" and paste`
          ]
        };
      case 'android':
        return {
          icon: <Smartphone className="w-4 h-4" />,
          title: "Android",
          steps: [
            `Tap "Download Media"`,
            "Find in Downloads or Gallery",
            `Open ${platformName}`,
            "Create new post, select media",
            `Tap "Copy Caption" and paste`
          ]
        };
      case 'macos':
        return {
          icon: <Monitor className="w-4 h-4" />,
          title: "macOS",
          steps: [
            `Click "Download Media"`,
            "Find in Downloads folder",
            `Open ${platformName} (web or transfer to phone)`,
            `Click "Copy Caption" and paste`
          ]
        };
      case 'windows':
        return {
          icon: <Monitor className="w-4 h-4" />,
          title: "Windows",
          steps: [
            `Click "Download Media"`,
            "Find in Downloads folder",
            `Open ${platformName} (web or transfer to phone)`,
            `Click "Copy Caption" and paste`
          ]
        };
      default:
        return {
          icon: <Download className="w-4 h-4" />,
          title: "Download",
          steps: [
            "Download the media",
            `Open ${platformName}`,
            "Upload and paste caption"
          ]
        };
    }
  };
  
  const instructions = getInstructions();
  
  // Minimal variant - just buttons
  if (variant === 'minimal') {
    return (
      <div className="flex gap-2" data-testid="download-panel-minimal">
        <Button
          onClick={handleDownload}
          disabled={downloading || !mediaUrl}
          size="sm"
          className="bg-ink-950 hover:bg-ink-900 gap-1"
          data-testid="download-btn"
        >
          <Download className="w-3 h-3" />
          Download
        </Button>
        <Button
          onClick={handleCopyAll}
          size="sm"
          variant="outline"
          className="border-ink-200 text-ink-700 hover:bg-ink-100 gap-1"
          data-testid="copy-all-btn"
        >
          <Copy className="w-3 h-3" />
          Copy
        </Button>
      </div>
    );
  }
  
  // Compact variant - buttons with basic info
  if (variant === 'compact') {
    return (
      <div className="p-4 rounded-xl bg-ink-50 border border-ink-200 space-y-3" data-testid="download-panel-compact">
        <div className="flex items-center gap-2 text-sm text-ink-700">
          <Download className="w-4 h-4 text-coral-500" />
          <span className="font-medium">Manual Upload</span>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={handleDownload}
            disabled={downloading || !mediaUrl}
            className="bg-ink-950 hover:bg-ink-900 gap-2 h-10"
            data-testid="download-btn"
          >
            {downloading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download
          </Button>
          <Button
            onClick={handleCopyAll}
            className="bg-coral-500 hover:bg-coral-600 gap-2 h-10"
            data-testid="copy-all-btn"
          >
            <Copy className="w-4 h-4" />
            Copy Caption
          </Button>
        </div>
        
        {isMobile && (
          <Button
            onClick={handleShare}
            variant="outline"
            className="w-full border-ink-200 text-ink-700 hover:bg-ink-100 gap-2"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4" />
            Share
          </Button>
        )}
      </div>
    );
  }
  
  // Full variant - complete panel with instructions
  return (
    <div 
      className={`p-5 rounded-xl border space-y-4 ${
        platform === 'instagram'
          ? 'bg-gradient-to-br from-purple-900/20 to-pink-900/20 border-pink-500/30'
          : 'bg-gradient-to-br from-slate-800 to-slate-900 border-ink-200'
      }`}
      data-testid="download-panel-full"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-ink-950 font-semibold flex items-center gap-2">
          <Download className="w-5 h-5 text-coral-500" />
          Manual Upload to {platform === 'instagram' ? 'Instagram' : 'TikTok'}
        </h4>
        {productUrl && (
          <a 
            href={productUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-ink-500 hover:text-ink-950 flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            Product
          </a>
        )}
      </div>
      
      {/* Main Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          onClick={handleDownload}
          disabled={downloading || !mediaUrl}
          className="bg-ink-950 hover:bg-ink-900 gap-2 h-12"
          data-testid="download-media-btn"
        >
          {downloading ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          Download Media
        </Button>
        <Button
          onClick={handleCopyAll}
          className="bg-coral-500 hover:bg-coral-600 gap-2 h-12"
          data-testid="copy-caption-btn"
        >
          <Copy className="w-4 h-4" />
          Copy All Text
        </Button>
      </div>
      
      {/* Secondary Actions */}
      <div className="flex gap-2">
        <Button
          onClick={handleCopyCaption}
          variant="outline"
          size="sm"
          className="flex-1 border-ink-200 text-ink-700 hover:bg-ink-100 gap-1"
          data-testid="copy-caption-only-btn"
        >
          {copiedCaption ? <CheckCircle2 className="w-3 h-3 text-coral-500" /> : <Copy className="w-3 h-3" />}
          Caption Only
        </Button>
        {hashtags?.length > 0 && (
          <Button
            onClick={handleCopyHashtags}
            variant="outline"
            size="sm"
            className="flex-1 border-ink-200 text-ink-700 hover:bg-ink-100 gap-1"
            data-testid="copy-hashtags-btn"
          >
            {copiedHashtags ? <CheckCircle2 className="w-3 h-3 text-coral-500" /> : <Copy className="w-3 h-3" />}
            Hashtags Only
          </Button>
        )}
        {isMobile && (
          <Button
            onClick={handleShare}
            variant="outline"
            size="sm"
            className="border-ink-200 text-ink-700 hover:bg-ink-100"
            data-testid="share-btn"
          >
            <Share2 className="w-3 h-3" />
          </Button>
        )}
      </div>
      
      {/* Platform-Specific Instructions */}
      <div className="pt-3 border-t border-ink-200/50">
        <div className="flex items-center gap-2 mb-2 text-sm text-ink-500">
          {instructions.icon}
          <span>{instructions.title} Instructions:</span>
        </div>
        <ol className="space-y-1.5">
          {instructions.steps.map((step, i) => (
            <li key={i} className="text-xs text-ink-500 flex items-start gap-2">
              <span className="text-coral-500 font-mono">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>
      
      {/* iOS/Device tip */}
      {userPlatform === 'ios' && (
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-xs text-blue-300">
            💡 <strong>Tip:</strong> After downloading, find the media in your Photos app under "Recents"
          </p>
        </div>
      )}
    </div>
  );
};

export default ManualDownloadPanel;
export { downloadMedia, copyToClipboard, shareMedia, detectPlatform };
